import React, { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import Loading from "../../../../common-components/Loading";
import { getTMProfitabilityDetailsAPI } from "../../../../api/reports-api";
import {
  toggleSortKey,
  compareBySortKeys,
  createSortThClick,
  WaterfallThLabel,
} from "../waterfall-eac/waterfallTableUtils";
import { calcFilterPos, FILTER_PANEL_WIDTH_NUMERIC } from "../waterfall-eac/filterPopoverUtils";
import { NumericFilterContent, TextFilterContent, OpDropdownContent, fpBase } from "../waterfall-eac/tableColumnFilterShared";
import { getGlobalPeriodHiddenKeys } from "../pl-by-program/plTimeFilterUtils";
import {
  TABLE_FONT,
  TH_BG,
  TH_TEXT,
  TH_SHADOW,
  CELL_FG,
  CELL_FG_STRONG,
  EVEN_BG,
  ODD_BG,
  TOTAL_BG,
  Z_HEAD,
  Z_STICKY,
  TH_HEIGHT,
  ROW_HEIGHT,
  ACCENT_COLOR,
  CELL_FG_MUTED,
  TH_BG_BAND,
  TH_ACTUAL_BG,
  TH_ACTUAL_TEXT,
} from "../../reportTableConfig";

const FONT = TABLE_FONT;
const TOTAL_FG = CELL_FG_STRONG;
const SC_ACCENT = ACCENT_COLOR;
const CH = TH_HEIGHT;
const RH = ROW_HEIGHT;

/* ══════════════════════════════════════════════════════════════
   Months
══════════════════════════════════════════════════════════════ */
const MONTH_KEYS   = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];
const MONTH_LABELS = { jan:"Jan",feb:"Feb",mar:"Mar",apr:"Apr",may:"May",jun:"Jun",jul:"Jul",aug:"Aug",sep:"Sep",oct:"Oct",nov:"Nov",dec:"Dec" };
const MONTH_NUM    = { jan:1,feb:2,mar:3,apr:4,may:5,jun:6,jul:7,aug:8,sep:9,oct:10,nov:11,dec:12 };

function mapTriplet(src) {
  if (!src) return { rev: 0, lab: 0, gp: 0 };
  return {
    rev: src.revenue ?? 0,
    lab: src.labor ?? 0,
    gp:  src.gp ?? 0,
  };
}

function mapMonths(apiMonths) {
  const out = {};
  MONTH_KEYS.forEach((k) => { out[k] = mapTriplet(apiMonths?.[k]); });
  return out;
}

function buildProjects(rows) {
  const map = new Map();
  (rows || []).forEach((row) => {
    const key = row.projId;
    if (!map.has(key)) {
      map.set(key, {
        projectId:   row.projId,
        projectName: row.projectName || "",
        employees:   [],
      });
    }
    map.get(key).employees.push({
      empId:   row.empId,
      empName: row.empName,
      months:  mapMonths(row.months),
      total:   mapTriplet(row.total),
    });
  });
  return [...map.values()].map((p) => ({
    ...p,
    totals: sumMonths(p.employees),
  }));
}

/* ══════════════════════════════════════════════════════════════
   Computed totals
══════════════════════════════════════════════════════════════ */
function sumMonths(employees) {
  const out = {};
  MONTH_KEYS.forEach((key) => {
    let rev = 0, lab = 0;
    employees.forEach((e) => { rev += e.months[key]?.rev || 0; lab += e.months[key]?.lab || 0; });
    out[key] = { rev: parseFloat(rev.toFixed(2)), lab: parseFloat(lab.toFixed(2)), gp: parseFloat((rev - lab).toFixed(2)) };
  });
  let tRev = 0, tLab = 0;
  MONTH_KEYS.forEach((key) => { tRev += out[key].rev; tLab += out[key].lab; });
  out.total = { rev: parseFloat(tRev.toFixed(2)), lab: parseFloat(tLab.toFixed(2)), gp: parseFloat((tRev - tLab).toFixed(2)) };
  return out;
}

function mapGrandTotal(apiGT) {
  const out = {};
  MONTH_KEYS.forEach((key) => { out[key] = mapTriplet(apiGT?.[key]); });
  let tRev = 0, tLab = 0;
  MONTH_KEYS.forEach((key) => { tRev += out[key].rev; tLab += out[key].lab; });
  out.total = { rev: parseFloat(tRev.toFixed(2)), lab: parseFloat(tLab.toFixed(2)), gp: parseFloat((tRev - tLab).toFixed(2)) };
  return out;
}

/** Total column — summed over just the currently visible (month/quarter-filtered) months,
    not the fixed full-year total baked in at fetch time. `monthMap` is keyed by month (a
    project's `totals`, an employee's `months`, or the grand total map all share this shape). */
function sumTriplets(monthMap, months) {
  let rev = 0, lab = 0;
  months.forEach((m) => {
    const t = monthMap?.[m.key];
    if (t) { rev += t.rev || 0; lab += t.lab || 0; }
  });
  rev = parseFloat(rev.toFixed(2));
  lab = parseFloat(lab.toFixed(2));
  return { rev, lab, gp: parseFloat((rev - lab).toFixed(2)) };
}


/* ══════════════════════════════════════════════════════════════
   Fixed column definitions
══════════════════════════════════════════════════════════════ */
const FIXED_COLS_DEF = [
  { key: "projId",   label: "Project ID",   w: 170, align: "left" },
  { key: "projName", label: "Project Name", w: 200, align: "left" },
  { key: "empId",    label: "Emp ID",       w: 110, align: "left" },
  { key: "empName",  label: "Emp Name",     w: 180, align: "left" },
];

const METRIC_COL_W = { rev: 96, lab: 130, gp: 120 };
const METRIC_SUBS = ["rev", "lab", "gp"];
const METRIC_LABELS = { rev: "Revenue", lab: "Labor Cost", gp: "Gross Profit" };
const METRIC_GROUP_W = METRIC_SUBS.reduce((sum, key) => sum + METRIC_COL_W[key], 0);

/* ══════════════════════════════════════════════════════════════
   Palette — from reportTableConfig
══════════════════════════════════════════════════════════════ */
const GROUP_BG  = "#EEF4FF";
const CARD_META = { fontSize: 12, color: CELL_FG_MUTED, marginTop: 4, fontFamily: FONT };
const GH = 32;

/* ══════════════════════════════════════════════════════════════
   Header style functions
══════════════════════════════════════════════════════════════ */
const thCore = (top, h, z, padding) => ({
  position: "sticky", top, zIndex: z, height: h, lineHeight: 1.25,
  boxSizing: "border-box", background: TH_BG, color: TH_TEXT,
  verticalAlign: "middle", padding, whiteSpace: "nowrap", border: "none",
  boxShadow: TH_SHADOW,
});
const thFixedGroup = (w) => ({
  ...thCore(0, GH, Z_HEAD + 1, "10px 16px"),
  left: 0, width: w, minWidth: w, maxWidth: w,
});
const thFixedCol = (col, isLast) => ({
  ...thCore(GH, CH, Z_HEAD + 1, "10px 14px"),
  left: col.left, width: col.w, minWidth: col.w, maxWidth: col.w,
  fontSize: 11, fontWeight: 500, textAlign: col.align,
  boxShadow: isLast ? `4px 0 8px -2px rgba(0,0,0,0.18), ${TH_SHADOW}` : TH_SHADOW,
});
const thScrollGroup = (isActual) => ({
  ...thCore(0, GH, Z_HEAD, "10px 10px"),
  fontSize: 10, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase",
  textAlign: "center",
  ...(isActual
    ? { background: TH_ACTUAL_BG, color: TH_ACTUAL_TEXT }
    : { color: TH_TEXT }),
});
const thScrollCol = (isActual) => ({
  ...thCore(GH, CH, Z_HEAD, "10px 8px"),
  fontSize: 11, textAlign: "right",
  fontWeight: isActual ? 700 : 500,
  ...(isActual
    ? { background: TH_ACTUAL_BG, color: TH_ACTUAL_TEXT }
    : { color: TH_TEXT }),
});

const tdBase = (bg, align = "right", fw = 400) => ({
  boxSizing: "border-box", background: bg, textAlign: align, fontWeight: fw,
  padding: "7px 8px", fontSize: 12, color: CELL_FG, whiteSpace: "nowrap",
  verticalAlign: "middle", fontVariantNumeric: "tabular-nums", borderBottom: "1px solid #E2E8F0",
});
const tdFixed = (bg, col, fw = 400, isLast = false) => ({
  ...tdBase(bg, col.align, fw),
  position: "sticky", left: col.left, zIndex: Z_STICKY,
  width: col.w, minWidth: col.w, maxWidth: col.w,
  boxShadow: isLast ? "4px 0 8px -2px rgba(0,0,0,0.06)" : undefined,
});
const tdTotal = (col, isLast = false) => ({
  ...tdFixed(TOTAL_BG, col, 700, isLast), color: TOTAL_FG, borderBottom: "1px solid #CBD5E1",
});
const tdTotalScroll = () => ({
  ...tdBase(TOTAL_BG, "right", 600), color: TOTAL_FG, borderBottom: "1px solid #CBD5E1",
});
const tdGroup = (col, isLast = false) => ({
  ...tdFixed(GROUP_BG, col, 600, isLast), color: "#1E3A5F", borderBottom: "1px solid #DBEAFE",
});
const tdGroupScroll = () => ({
  ...tdBase(GROUP_BG, "right", 500), color: "#1E3A5F", borderBottom: "1px solid #DBEAFE",
});

/* ══════════════════════════════════════════════════════════════
   Filter icon button style
══════════════════════════════════════════════════════════════ */
const filterIconBtn = (active, isActual = false) => ({
  flexShrink: 0, width: 16, height: 16, border: "none",
  background: active ? "rgba(165,180,252,0.18)" : "transparent",
  cursor: "pointer", padding: 0, borderRadius: 3,
  color: isActual ? TH_ACTUAL_TEXT : (active ? "#A5B4FC" : TH_TEXT),
  opacity: active ? 1 : 0.5,
  display: "inline-flex", alignItems: "center", justifyContent: "center", verticalAlign: "middle",
});

/* ══════════════════════════════════════════════════════════════
   Operator filter constants
══════════════════════════════════════════════════════════════ */
const OP_LABELS  = { gt: ">", gte: "≥", lt: "<", lte: "≤", eq: "=", neq: "≠" };
const OP_ENTRIES = [
  { key: "gt",  label: "> Greater than" },
  { key: "gte", label: "≥ Greater than or equal" },
  { key: "lt",  label: "< Less than" },
  { key: "lte", label: "≤ Less than or equal" },
  { key: "eq",  label: "= Equals" },
  { key: "neq", label: "≠ Not equal" },
];

function applyNumFilter(rawVal, filter) {
  if (!filter || filter.value === "" || filter.value === undefined) return true;
  const v  = rawVal == null ? 0 : Number(rawVal);
  const fv = Number(filter.value);
  switch (filter.op || "gt") {
    case "gt":  return v > fv;
    case "gte": return v >= fv;
    case "lt":  return v < fv;
    case "lte": return v <= fv;
    case "eq":  return v === fv;
    case "neq": return v !== fv;
    default:    return true;
  }
}

/* ══════════════════════════════════════════════════════════════
   Icons
══════════════════════════════════════════════════════════════ */
function FilterFunnelIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

function ChevronIcon({ open }) {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"
      style={{ transition: "transform 0.18s ease", transform: open ? "rotate(90deg)" : "rotate(0deg)", flexShrink: 0 }}>
      <path d="M3 2l4 3-4 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ══════════════════════════════════════════════════════════════
   Filter/picker panel styles
══════════════════════════════════════════════════════════════ */
const filterPanelSt = {
  position: "fixed", zIndex: 99, background: "#fff",
  border: "1px solid #E2E8F0", borderRadius: 8,
  boxShadow: "0 8px 24px rgba(0,0,0,0.12)", padding: "10px 12px", minWidth: 190, fontFamily: FONT,
};
const filterWrap      = { display: "flex", alignItems: "center", gap: 3, height: 26 };
const filterTextInput = { flex: 1, height: 26, border: "1.5px solid #CBD5E1", borderRadius: 5, fontSize: 11, padding: "0 7px", outline: "none", fontFamily: FONT, background: "#fff", color: "#111827" };
const filterNumInput  = { flex: 1, height: 26, border: "1.5px solid #CBD5E1", borderRadius: 5, fontSize: 11, padding: "0 5px", outline: "none", fontFamily: FONT, background: "#fff", color: "#111827", textAlign: "right" };
const opBtnSt  = (active) => ({ flexShrink: 0, width: 22, height: 26, border: `1.5px solid ${active ? "#4F46E5" : "#CBD5E1"}`, borderRadius: 5, background: active ? "#4F46E5" : "#fff", fontSize: 11, fontWeight: 700, color: active ? "#fff" : "#4F46E5", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, fontFamily: FONT });
const clearBtnSt = { flexShrink: 0, width: 18, height: 18, border: "none", borderRadius: "50%", background: "#E2E8F0", fontSize: 9, color: "#64748B", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 };
const opDropSt = { position: "fixed", zIndex: 99, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", minWidth: 190, overflow: "hidden", fontFamily: FONT };
const colsBtnSt = (active) => ({ display: "inline-flex", alignItems: "center", gap: 6, height: 30, padding: "0 12px", borderRadius: 6, border: `1px solid ${active ? "#C7D2FE" : "#E2E8F0"}`, background: active ? "#EEF2FF" : "#fff", fontSize: 12, fontWeight: 500, color: active ? "#4F46E5" : "#374151", cursor: "pointer", fontFamily: FONT });
const hiddenBadgeSt = { display: "inline-flex", alignItems: "center", height: 18, padding: "0 7px", borderRadius: 10, background: "#4F46E5", color: "#fff", fontSize: 11, fontWeight: 600 };
const colPickerSt = { position: "absolute", top: "calc(100% + 4px)", right: 0, zIndex: 1200, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 10, boxShadow: "0 8px 28px rgba(0,0,0,0.12)", minWidth: 200, maxHeight: 420, overflowY: "auto", fontFamily: FONT };
const colPickerHdr = { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px 8px", borderBottom: "1px solid #F3F4F6" };
const colPickerItem = { display: "flex", alignItems: "center", gap: 9, padding: "7px 14px", cursor: "pointer", fontSize: 12, color: "#374151", userSelect: "none", borderBottom: "1px solid #F9FAFB" };

/* ══════════════════════════════════════════════════════════════
   Pagination styles
══════════════════════════════════════════════════════════════ */
const pgBar    = { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 20px", borderTop: "1px solid #E2E8F0", background: "#FAFAFA", gap: 12, flexWrap: "wrap" };
const pgInfo   = { fontSize: 12, color: "#6B7280", fontFamily: FONT, whiteSpace: "nowrap" };
const pgRight  = { display: "flex", alignItems: "center", gap: 8 };
const pgCtrl   = { display: "flex", alignItems: "center", gap: 3 };
const pgSzLbl  = { fontSize: 12, color: "#6B7280", fontFamily: FONT, whiteSpace: "nowrap" };
const pgSzSel  = { height: 28, border: "1px solid #E2E8F0", borderRadius: 6, fontSize: 12, color: "#374151", padding: "0 6px", background: "#fff", cursor: "pointer", fontFamily: FONT };
const pgBtn    = (dis) => ({ minWidth: 30, height: 28, padding: "0 6px", border: "1px solid #E2E8F0", borderRadius: 6, background: "#fff", color: dis ? "#94A3B8" : "#374151", fontSize: 12, fontWeight: 500, cursor: dis ? "default" : "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", fontFamily: FONT, opacity: dis ? 0.35 : 1 });
const pgBtnAct = { minWidth: 30, height: 28, padding: "0 6px", border: `1px solid ${TH_BG_BAND}`, borderRadius: 6, background: TH_BG_BAND, color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", fontFamily: FONT };
const pgDots   = { fontSize: 12, color: "#6B7280", padding: "0 4px", userSelect: "none" };

const IcoFirst = () => <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M9 10L6 7l3-3M5 10V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>;
const IcoPrev  = () => <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M8.5 10.5L5 7l3.5-3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>;
const IcoNext  = () => <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M5.5 3.5L9 7l-3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>;
const IcoLast  = () => <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M5 10l3-3-3-3M9 10V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>;

/* ══════════════════════════════════════════════════════════════
   Formatters
══════════════════════════════════════════════════════════════ */
const fmtMoney = (v) => {
  if (v === null || v === undefined) return "";
  if (v === 0) return "—";
  const abs = Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return v < 0 ? `($${abs})` : `$${abs}`;
};

/* ══════════════════════════════════════════════════════════════
   TripleCells helper
══════════════════════════════════════════════════════════════ */
function TripleCells({ d, cellStyle }) {
  return (
    <>
      <td style={cellStyle}>{fmtMoney(d?.rev)}</td>
      <td style={cellStyle}>{fmtMoney(d?.lab)}</td>
      <td style={cellStyle}>{fmtMoney(d?.gp)}</td>
    </>
  );
}

/* ══════════════════════════════════════════════════════════════
   Card styles
══════════════════════════════════════════════════════════════ */
const CARD  = { background: "#FFFFFF", borderRadius: 12, border: "1px solid #EBEBEB", overflow: "visible", display: "flex", flexDirection: "column", marginBottom: 24, boxShadow: "0 1px 2px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)" };
const HEAD  = { padding: "16px 20px 12px", borderBottom: "1px solid #E2E8F0", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, position: "relative", zIndex: 20, overflow: "visible", flexShrink: 0, background: "#fff", borderTopLeftRadius: 12, borderTopRightRadius: 12 };
const CARD_BODY = { overflow: "hidden", borderBottomLeftRadius: 12, borderBottomRightRadius: 12 };
const TITLE = { fontSize: 14, fontWeight: 600, color: "#111827", letterSpacing: "-0.01em", fontFamily: FONT, margin: 0 };

/* ══════════════════════════════════════════════════════════════
   Main component
══════════════════════════════════════════════════════════════ */
export default function Details({ filters, onMetaLoaded }) {
  const [loading,     setLoading]     = useState(true);
  const [projects,    setProjects]    = useState([]);
  const [grandTotal,  setGrandTotal]  = useState({});
  const [actualThru,  setActualThru]  = useState(0);
  const [fiscalYear,  setFiscalYear]  = useState(null);
  const [expanded,    setExpanded]    = useState(new Set());

  /* Month/quarter never reach the API (postTMProfitability strips them) — the full year is
     fetched once and narrowed to the selected month/quarter client-side below, so this key
     excludes them to keep the fetch from re-running when only month/quarter change. */
  const apiFilterKey = JSON.stringify({ ...filters, monthName: undefined, quarter: undefined });
  const globalPeriodHidden = useMemo(
    () => getGlobalPeriodHiddenKeys(filters, "Month", MONTH_KEYS),
    [filters?.monthName, filters?.quarter],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getTMProfitabilityDetailsAPI(filters)
      .then((res) => {
        if (cancelled) return;
        const data = res?.data ?? res;
        const built = buildProjects(data?.rows ?? []);
        setProjects(built);
        setGrandTotal(mapGrandTotal(data?.grandTotal ?? {}));
        setActualThru(data?.actualThroughMonth ?? 0);
        setFiscalYear(data?.fiscalYear ?? null);
        setExpanded(new Set(built.map((p) => p.projectId)));
        setPage(1);
        setLoading(false);
        onMetaLoaded?.({
          actualThroughMonth: data?.actualThroughMonth,
          fiscalYear: data?.fiscalYear,
        });
      })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [apiFilterKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const monthsMeta = useMemo(
    () => MONTH_KEYS.map((key) => ({
      key,
      label: MONTH_LABELS[key],
      isActual: MONTH_NUM[key] <= actualThru,
    })),
    [actualThru],
  );

  /* ── expand/collapse ── */
  const allExpanded = projects.length > 0 && projects.every((p) => expanded.has(p.projectId));
  const toggle = (id) => setExpanded((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setExpanded(allExpanded ? new Set() : new Set(projects.map((p) => p.projectId)));

  /* ── show/hide columns ── */
  const [hiddenCols, setHiddenCols] = useState(new Set());
  const [colPickerOpen, setColPickerOpen] = useState(false);
  const colPickerRef = useRef(null);

  const toggleCol = useCallback((key) => {
    setHiddenCols((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  }, []);

  /* ── pagination ── */
  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(5);

  /* ── table filters (text for fixed, numeric for month sub-cols) ── */
  const [textFilters, setTextFilters] = useState({});
  const [numFilters,  setNumFilters]  = useState({});
  const [sortKeys,    setSortKeys]    = useState([]);
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const [filterPos,     setFilterPos]     = useState({ top: 0, left: 0 });
  const [openOpKey,     setOpenOpKey]     = useState(null);
  const [opPos,         setOpPos]         = useState({ top: 0, left: 0 });

  const filterBtnRefs  = useRef({});
  const opBtnRefs      = useRef({});
  const openFilterKeyRef = useRef(null);
  const openOpKeyRef     = useRef(null);

  const toggleOpDropdown = useCallback((key) => {
    if (openOpKey === key) { setOpenOpKey(null); return; }
    const btn = opBtnRefs.current[key];
    if (btn) {
      const r = btn.getBoundingClientRect();
      setOpPos({ top: r.bottom + 4, left: r.left });
    }
    setOpenOpKey(key);
  }, [openOpKey]);

  const getFilterLabel = useCallback((key) => {
    const fixed = FIXED_COLS_DEF.find((c) => c.key === key);
    if (fixed) return fixed.label;
    const us = key.indexOf("_");
    if (us > 0) {
      const mk = key.slice(0, us);
      const sub = key.slice(us + 1);
      const month = MONTH_LABELS[mk] || mk;
      if (METRIC_LABELS[sub]) return `${month} ${METRIC_LABELS[sub]}`;
    }
    return key;
  }, []);

  const toggleFilter = useCallback((key) => {
    const next = openFilterKey === key ? null : key;
    openFilterKeyRef.current = next;
    setOpenFilterKey(next);
    setOpenOpKey(null);
    if (next && filterBtnRefs.current[next]) {
      setFilterPos(calcFilterPos(filterBtnRefs.current[next], FILTER_PANEL_WIDTH_NUMERIC));
    }
  }, [openFilterKey]);

  /* ── click-outside: close filter panel and column picker ── */
  useEffect(() => {
    function handleDown(e) {
      if (e.target.closest("[data-filter-btn]")) return;
      if (openFilterKeyRef.current) { openFilterKeyRef.current = null; setOpenFilterKey(null); setOpenOpKey(null); }
      if (colPickerRef.current && !colPickerRef.current.contains(e.target)) setColPickerOpen(false);
    }
    document.addEventListener("mousedown", handleDown);
    return () => document.removeEventListener("mousedown", handleDown);
  }, []);

  const isFilterActive = useCallback((key) => {
    if (textFilters[key] && textFilters[key] !== "") return true;
    if (numFilters[key] && numFilters[key].value !== "" && numFilters[key].value !== undefined) return true;
    return false;
  }, [textFilters, numFilters]);

  const getSortColumnLabel = useCallback((key) => {
    const fixed = FIXED_COLS_DEF.find((c) => c.key === key);
    return fixed ? fixed.label : key;
  }, []);

  const getProjectSortValue = useCallback((proj, key) => {
    if (key === "projId") return proj.projectId ?? "";
    if (key === "projName") return proj.projectName ?? "";
    if (key === "empId") return proj.employees[0]?.empId ?? "";
    if (key === "empName") return proj.employees[0]?.empName ?? "";
    return "";
  }, []);

  const getEmployeeSortValue = useCallback((emp, key) => {
    if (key === "empId") return emp.empId ?? "";
    if (key === "empName") return emp.empName ?? "";
    return "";
  }, []);

  const handleSort = useCallback((colKey) => {
    setSortKeys((prev) => toggleSortKey(prev, colKey));
  }, []);

  const onThClick = useMemo(() => createSortThClick(handleSort), [handleSort]);

  useEffect(() => { setPage(1); }, [textFilters, numFilters]);

  /* ── derived: visible fixed cols ── */
  const stickyCols = useMemo(() => {
    let fl = 0;
    return FIXED_COLS_DEF.filter((c) => !hiddenCols.has(c.key)).map((c) => {
      const out = { ...c, left: fl };
      fl += c.w;
      return out;
    });
  }, [hiddenCols]);

  const stickyW    = stickyCols.reduce((s, c) => s + c.w, 0);
  const isLastSticky = (ci) => ci === stickyCols.length - 1;

  /* ── visible months ── */
  const visibleMonths = useMemo(
    () =>
      monthsMeta.filter((m) => {
        if (globalPeriodHidden.has(m.key)) return false;
        /* A selected global month/quarter filter overrides the table-level
           column picker — e.g. hiding Jan via the picker then selecting Jan
           globally should bring the Jan column back, not keep it hidden. */
        return globalPeriodHidden.size > 0 || !hiddenCols.has(m.key);
      }),
    [monthsMeta, hiddenCols, globalPeriodHidden],
  );

  /* ── table width ── */
  const TABLE_W = stickyW + (visibleMonths.length + 1) * METRIC_GROUP_W;

  /* ── filtered projects ── */
  const filteredProjects = useMemo(() => {
    return projects.filter((proj) => {
      for (const c of stickyCols) {
        const tf = textFilters[c.key];
        if (tf && tf !== "") {
          const val = c.key === "projId" ? proj.projectId : (c.key === "projName" ? proj.projectName : "");
          if (!String(val ?? "").toLowerCase().includes(tf.toLowerCase())) return false;
        }
      }
      for (const m of visibleMonths) {
        for (const sub of ["rev", "lab", "gp"]) {
          const k = `${m.key}_${sub}`;
          const nf = numFilters[k];
          if (nf && nf.value !== "" && nf.value !== undefined) {
            if (!applyNumFilter(proj.totals[m.key]?.[sub], nf)) return false;
          }
        }
      }
      return true;
    });
  }, [projects, stickyCols, visibleMonths, textFilters, numFilters]);

  const sortedProjects = useMemo(() => {
    if (sortKeys.length === 0) return filteredProjects;
    const sortEmployees = sortKeys.some((s) => s.key === "empId" || s.key === "empName");
    let result = filteredProjects.map((proj) => ({
      ...proj,
      employees: sortEmployees
        ? [...proj.employees].sort((a, b) => compareBySortKeys(a, b, sortKeys, getEmployeeSortValue))
        : proj.employees,
    }));
    result = [...result].sort((a, b) => compareBySortKeys(a, b, sortKeys, getProjectSortValue));
    return result;
  }, [filteredProjects, sortKeys, getProjectSortValue, getEmployeeSortValue]);

  const hasClientFilters = useMemo(
    () => Object.values(textFilters).some((v) => v && v !== "")
      || Object.values(numFilters).some((f) => f?.value !== "" && f?.value !== undefined),
    [textFilters, numFilters],
  );

  /* ── pagination ── */
  const totalPages  = Math.max(1, Math.ceil(sortedProjects.length / pageSize));
  const clampedPage = Math.min(page, totalPages);
  const pageProjects = sortedProjects.slice((clampedPage - 1) * pageSize, clampedPage * pageSize);

  /* ── grand total for visible (filtered) projects ── */
  const visibleGrandTotal = useMemo(() => {
    if (!hasClientFilters) return grandTotal;
    const out = {};
    MONTH_KEYS.forEach((key) => {
      let rev = 0, lab = 0;
      filteredProjects.forEach((p) => { rev += p.totals[key].rev; lab += p.totals[key].lab; });
      out[key] = { rev: parseFloat(rev.toFixed(2)), lab: parseFloat(lab.toFixed(2)), gp: parseFloat((rev - lab).toFixed(2)) };
    });
    let tRev = 0, tLab = 0;
    MONTH_KEYS.forEach((key) => { tRev += out[key].rev; tLab += out[key].lab; });
    out.total = { rev: parseFloat(tRev.toFixed(2)), lab: parseFloat(tLab.toFixed(2)), gp: parseFloat((tRev - tLab).toFixed(2)) };
    return out;
  }, [filteredProjects, grandTotal, hasClientFilters]);

  /* ── page buttons ── */
  const pageNums = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const p = clampedPage, pages = [];
    pages.push(1);
    if (p > 3) pages.push("...");
    for (let i = Math.max(2, p - 1); i <= Math.min(totalPages - 1, p + 1); i++) pages.push(i);
    if (p < totalPages - 2) pages.push("...");
    pages.push(totalPages);
    return pages;
  }, [totalPages, clampedPage]);

  const tableStyle = { borderCollapse: "collapse", borderSpacing: 0, tableLayout: "fixed", fontFamily: FONT, fontSize: 12, width: TABLE_W, minWidth: TABLE_W };

  const subtitleFy = fiscalYear ?? filters?.year ?? new Date().getFullYear();

  if (loading) return <Loading />;

  return (
    <div style={{ fontFamily: FONT }}>
      <div style={CARD}>
        {/* ── Card header ── */}
        <div style={HEAD}>
          <div>
            <h2 style={TITLE}>Details</h2>
            <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2 }}>Revenue · Labor Cost · Gross Profit by Project &amp; Employee · FY-{subtitleFy}</div>
            {sortKeys.length > 0 && (
              <div style={CARD_META}>
                <span style={{ color: SC_ACCENT, fontWeight: 500 }}>
                  Sorted by {sortKeys.map((s) => getSortColumnLabel(s.key)).join(", ")}
                </span>
              </div>
            )}
          </div>
          {/* Show/hide columns button */}
          <div style={{ position: "relative" }} ref={colPickerRef}>
            <button style={colsBtnSt(hiddenCols.size > 0)} onClick={() => setColPickerOpen((p) => !p)}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="9" y1="3" x2="9" y2="21"/><line x1="15" y1="3" x2="15" y2="21"/></svg>
              Columns
              {hiddenCols.size > 0 && <span style={hiddenBadgeSt}>{hiddenCols.size}</span>}
            </button>
            {colPickerOpen && (
              <div style={colPickerSt} onMouseDown={(e) => e.stopPropagation()}>
                <div style={colPickerHdr}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "#111827" }}>Show / Hide</span>
                  {hiddenCols.size > 0 && (
                    <button onClick={() => setHiddenCols(new Set())} style={{ fontSize: 11, color: "#4F46E5", background: "none", border: "none", cursor: "pointer", padding: 0 }}>Show All</button>
                  )}
                </div>
                {FIXED_COLS_DEF.map((c) => (
                  <div key={c.key} style={colPickerItem} onClick={() => toggleCol(c.key)}>
                    <input type="checkbox" readOnly checked={!hiddenCols.has(c.key)} style={{ accentColor: "#4F46E5", cursor: "pointer" }} />
                    <span>{c.label}</span>
                  </div>
                ))}
                <div style={{ padding: "4px 14px 6px", fontSize: 11, color: "#9CA3AF", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>Months</div>
                {MONTH_KEYS.map((key) => (
                  <div key={key} style={colPickerItem} onClick={() => toggleCol(key)}>
                    <input type="checkbox" readOnly checked={!hiddenCols.has(key)} style={{ accentColor: "#4F46E5", cursor: "pointer" }} />
                    <span>{MONTH_LABELS[key]}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={CARD_BODY}>
        {/* ── Table ── */}
        <div style={{ overflowX: "auto" }}>
          <table style={tableStyle}>
            <colgroup>
              {stickyCols.map((c) => <col key={c.key} style={{ width: c.w }} />)}
              {visibleMonths.map((m) => (
                <React.Fragment key={m.key}>
                  {METRIC_SUBS.map((sub) => <col key={sub} style={{ width: METRIC_COL_W[sub] }} />)}
                </React.Fragment>
              ))}
              {METRIC_SUBS.map((sub) => <col key={sub} style={{ width: METRIC_COL_W[sub] }} />)}
            </colgroup>

            <thead>
              {/* Row 1: fixed area shows the selected year + month group headers */}
              <tr style={{ height: GH }}>
                {stickyCols.length > 0 && (
                  <th colSpan={stickyCols.length} style={thFixedGroup(stickyW)}>
                    {filters?.year ? `Year - ${filters.year}` : ""}
                  </th>
                )}
                {visibleMonths.map((m) => (
                  <th key={m.key} colSpan={3} style={thScrollGroup(m.isActual)}>{m.label}</th>
                ))}
                <th colSpan={3} style={thScrollGroup(false)}>Total</th>
              </tr>
              {/* Row 2: individual fixed col labels + Rev / Labor / GP sub-col headers */}
              <tr style={{ height: CH }}>
                {stickyCols.map((c, ci) => (
                  <th key={c.key} style={{ ...thFixedCol(c, isLastSticky(ci)), cursor: "pointer" }} onClick={onThClick(c.key)}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: c.align === "right" ? "flex-end" : "space-between", gap: 4 }}>
                      {ci === 0 ? (
                        <>
                          <button type="button" onClick={(e) => { e.stopPropagation(); toggleAll(); }} title={allExpanded ? "Collapse all" : "Expand all"}
                            style={{ display: "inline-flex", alignItems: "center", background: "none", border: "none", color: "inherit", cursor: "pointer", padding: 0, flexShrink: 0 }}>
                            <ChevronIcon open={allExpanded} />
                          </button>
                          <WaterfallThLabel
                            label={c.label}
                            colKey={c.key}
                            sortKeys={sortKeys}
                            getLabel={getSortColumnLabel}
                            highlight={openFilterKey === c.key}
                            align={c.align}
                          />
                        </>
                      ) : (
                        <WaterfallThLabel
                          label={c.label}
                          colKey={c.key}
                          sortKeys={sortKeys}
                          getLabel={getSortColumnLabel}
                          highlight={openFilterKey === c.key}
                          align={c.align}
                        />
                      )}
                      <button ref={(el) => { filterBtnRefs.current[c.key] = el; }} data-filter-btn
                        style={filterIconBtn(isFilterActive(c.key) || openFilterKey === c.key)}
                        onClick={() => toggleFilter(c.key)} title="Filter">
                        <FilterFunnelIcon />
                      </button>
                    </div>
                  </th>
                ))}
                {visibleMonths.map((m) => (
                  <React.Fragment key={m.key}>
                    {METRIC_SUBS.map((sub) => {
                      const fk = `${m.key}_${sub}`;
                      return (
                        <th key={sub} style={thScrollCol(m.isActual)}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 3 }}>
                            <span>{METRIC_LABELS[sub]}</span>
                            <button ref={(el) => { filterBtnRefs.current[fk] = el; }} data-filter-btn
                              style={filterIconBtn(isFilterActive(fk) || openFilterKey === fk, m.isActual)}
                              onClick={() => toggleFilter(fk)} title={`Filter ${METRIC_LABELS[sub]}`}>
                              <FilterFunnelIcon />
                            </button>
                          </div>
                        </th>
                      );
                    })}
                  </React.Fragment>
                ))}
                {METRIC_SUBS.map((sub) => (
                  <th key={sub} style={thScrollCol(false)}>{METRIC_LABELS[sub]}</th>
                ))}
              </tr>
            </thead>

            <tbody>
              {/* Grand Total row */}
              <tr style={{ height: RH }}>
                {stickyCols.map((c, ci) => (
                  <td key={c.key} style={tdTotal(c, isLastSticky(ci))}>
                    {ci === 0 ? "Total" : ""}
                  </td>
                ))}
                {visibleMonths.map((m) => (
                  <TripleCells key={m.key} d={visibleGrandTotal[m.key]} cellStyle={tdTotalScroll()} />
                ))}
                <TripleCells d={sumTriplets(visibleGrandTotal, visibleMonths)} cellStyle={tdTotalScroll()} />
              </tr>

              {/* Project rows */}
              {pageProjects.map((proj) => {
                const isOpen = expanded.has(proj.projectId);
                return (
                  <React.Fragment key={proj.projectId}>
                    <tr style={{ height: RH, cursor: "pointer" }} onClick={() => toggle(proj.projectId)}>
                      {stickyCols.map((c, ci) => (
                        <td key={c.key} style={tdGroup(c, isLastSticky(ci))}>
                          {c.key === "projId" ? (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#1E3A5F" }}>
                              <ChevronIcon open={isOpen} />
                              <strong title={proj.projectId}>{proj.projectId}</strong>
                            </span>
                          ) : c.key === "projName" ? (
                            <span title={proj.projectName}>{proj.projectName}</span>
                          ) : ""}
                        </td>
                      ))}
                      {visibleMonths.map((m) => (
                        <TripleCells key={m.key} d={proj.totals[m.key]} cellStyle={tdGroupScroll()} />
                      ))}
                      <TripleCells d={sumTriplets(proj.totals, visibleMonths)} cellStyle={tdGroupScroll()} />
                    </tr>

                    {isOpen && proj.employees.map((emp, ei) => {
                      const bg = ei % 2 === 0 ? EVEN_BG : ODD_BG;
                      return (
                        <tr key={emp.empId + emp.empName} style={{ height: RH }}>
                          {stickyCols.map((c, ci) => (
                            <td key={c.key} style={tdFixed(bg, c, c.key === "empId" ? 600 : 400, isLastSticky(ci))}>
                              {c.key === "empId" ? <span style={{ paddingLeft: 20 }}>{emp.empId}</span>
                               : c.key === "empName" ? <span title={emp.empName}>{emp.empName}</span>
                               : ""}
                            </td>
                          ))}
                          {visibleMonths.map((m) => (
                            <TripleCells key={m.key} d={emp.months[m.key]} cellStyle={tdBase(bg)} />
                          ))}
                          <TripleCells d={sumTriplets(emp.months, visibleMonths)} cellStyle={tdBase(bg, "right", 600)} />
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ── */}
        <div style={pgBar}>
          <span style={pgInfo}>
            {filteredProjects.length === 0 ? "No projects" : `Projects ${(clampedPage - 1) * pageSize + 1}–${Math.min(clampedPage * pageSize, sortedProjects.length)} of ${sortedProjects.length}`}
          </span>
          <div style={pgRight}>
            <span style={pgSzLbl}>Projects Per Page:</span>
            <select style={pgSzSel} value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
              {[5, 10, 20, 50].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <div style={pgCtrl}>
              <button style={pgBtn(clampedPage === 1)} disabled={clampedPage === 1} onClick={() => setPage(1)}><IcoFirst /></button>
              <button style={pgBtn(clampedPage === 1)} disabled={clampedPage === 1} onClick={() => setPage((p) => p - 1)}><IcoPrev /></button>
              {pageNums.map((n, i) =>
                n === "..." ? <span key={`d${i}`} style={pgDots}>…</span>
                  : <button key={n} style={n === clampedPage ? pgBtnAct : pgBtn(false)} onClick={() => setPage(n)}>{n}</button>
              )}
              <button style={pgBtn(clampedPage === totalPages)} disabled={clampedPage === totalPages} onClick={() => setPage((p) => p + 1)}><IcoNext /></button>
              <button style={pgBtn(clampedPage === totalPages)} disabled={clampedPage === totalPages} onClick={() => setPage(totalPages)}><IcoLast /></button>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* ── Filter panel portal ── */}
      {openFilterKey && createPortal(
        <div style={{ ...fpBase, top: filterPos.top, left: filterPos.left }}
          onMouseDown={(e) => e.stopPropagation()}>
          {FIXED_COLS_DEF.some((c) => c.key === openFilterKey) ? (
            <TextFilterContent
              label={getFilterLabel(openFilterKey)}
              value={textFilters[openFilterKey] || ""}
              onChange={(v) => setTextFilters((p) => ({ ...p, [openFilterKey]: v }))}
              onClear={() => setTextFilters((p) => { const n = { ...p }; delete n[openFilterKey]; return n; })}
              placeholder="Filter…"
            />
          ) : (
            <NumericFilterContent
              label={getFilterLabel(openFilterKey)}
              value={numFilters[openFilterKey]?.value ?? ""}
              op={numFilters[openFilterKey]?.op || "gt"}
              onValueChange={(v) => setNumFilters((p) => ({ ...p, [openFilterKey]: { op: p[openFilterKey]?.op || "gt", value: v } }))}
              onClear={() => setNumFilters((p) => { const n = { ...p }; delete n[openFilterKey]; return n; })}
              onToggleOp={() => toggleOpDropdown(openFilterKey)}
              opBtnRef={(el) => { opBtnRefs.current[openFilterKey] = el; }}
            />
          )}
        </div>,
        document.body
      )}

      {/* ── Operator dropdown portal ── */}
      {openOpKey && createPortal(
        <OpDropdownContent
          activeOp={(numFilters[openOpKey]?.op) || "gt"}
          onSelect={(op) => {
            setNumFilters((p) => ({ ...p, [openOpKey]: { ...p[openOpKey], op } }));
            setOpenOpKey(null);
          }}
          style={{ top: opPos.top, left: opPos.left }}
        />,
        document.body
      )}
    </div>
  );
}
